import React, { useCallback, useState } from 'react';
import { FilterFormData } from '../stores/ui/uiStore';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilter: (filters: FilterFormData | null) => void;
  currentFilters?: FilterFormData | null;
  deckId: string;
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, onApplyFilter, currentFilters, deckId }) => {
  const [filters, setFilters] = useState<FilterFormData>(() => {
    // 현재 활성화된 필터가 있으면 그것을 사용, 없으면 기본값으로 초기화
    if (currentFilters) {
      return {
        ...currentFilters,
        // 기존 필터 데이터에 lastUpdated 필터가 없을 수 있으므로 기본값 설정
        lastUpdatedTimeFilter: currentFilters.lastUpdatedTimeFilter || 'ALL',
        lastUpdatedCustomTimeMinutes: currentFilters.lastUpdatedCustomTimeMinutes || undefined
      };
    }
    return {
      timeFilter: '24h',
      customTimeMinutes: undefined,
      lastUpdatedTimeFilter: 'ALL',
      lastUpdatedCustomTimeMinutes: undefined,
      poolCreatedAtAgeFilter: 'ALL',
      poolCreatedAtAgeMinutes: undefined,
      currentAfCnt: {},
      athAfCnt: {},
      atlMc: {},
      athMc: {},
      mc: {},
      currentTokenAmountInDecimals: {},
      athTokenAmountInDecimals: {},
      totalUsedUsd: {},
      devClusterCnt: {},
      cumulativeConvictionAtSameTimeRank: {}
    };
  });

  // 단위 변환 유틸리티 함수
  const convertUnitValue = (value: string, field: keyof FilterFormData): number | undefined => {
    if (value === '') return undefined;
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return undefined;

    // K (천 단위) 변환: atlMc, athMc, mc
    if (['atlMc', 'athMc', 'mc'].includes(field)) {
      return numValue * 1000;
    }
    
    // M (백만 단위) 변환: currentTokenAmountInDecimals, athTokenAmountInDecimals
    if (['currentTokenAmountInDecimals', 'athTokenAmountInDecimals'].includes(field)) {
      return numValue * 1000000;
    }
    
    // 단위가 없는 필드: currentAfCnt, athAfCnt, totalUsedUsd, devClusterCnt
    return numValue;
  };

  // 표시용 값 변환 함수 (필터 값에서 단위로 변환)
  const getDisplayValue = (value: number | undefined, field: keyof FilterFormData): string => {
    if (value === undefined) return '';
    
    // K (천 단위) 변환: atlMc, athMc, mc
    if (['atlMc', 'athMc', 'mc'].includes(field)) {
      return (value / 1000).toString();
    }
    
    // M (백만 단위) 변환: currentTokenAmountInDecimals, athTokenAmountInDecimals
    if (['currentTokenAmountInDecimals', 'athTokenAmountInDecimals'].includes(field)) {
      return (value / 1000000).toString();
    }
    
    // 단위가 없는 필드
    return value.toString();
  };

  const handleInputChange = (field: keyof FilterFormData, type: 'min' | 'max', value: string) => {
    const convertedValue = convertUnitValue(value, field);
    setFilters(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] as { min?: number; max?: number }),
        [type]: convertedValue
      }
    }));
  };

  const handleApplyFilter = useCallback(() => {
    onApplyFilter(filters);
    onClose();
  }, [filters, onApplyFilter, onClose]);

  const handleReset = () => {
    const initFilter = {
      timeFilter: '24h',
      customTimeMinutes: undefined,
      lastUpdatedTimeFilter: 'ALL',
      lastUpdatedCustomTimeMinutes: undefined,
      currentAfCnt: { min: undefined, max: undefined },
      athAfCnt: { min: undefined, max: undefined },
      atlMc: { min: undefined, max: undefined },
      athMc: { min: undefined, max: undefined },
      mc: { min: undefined, max: undefined },
      currentTokenAmountInDecimals: { min: undefined, max: undefined },
      athTokenAmountInDecimals: { min: undefined, max: undefined },
      totalUsedUsd: { min: undefined, max: undefined },
      legendaryAfCnt: { min: undefined, max: undefined },
      devClusterCnt: { min: undefined, max: undefined },
      cumulativeConvictionAtSameTimeRank: { min: undefined, max: undefined }
    };
    setFilters(initFilter as FilterFormData);
    onApplyFilter(initFilter as FilterFormData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={(e) => {
   
      onClose()
    }}>
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Filter Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
         
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current AF Count */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Current AF Count</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.currentAfCnt?.min || ''}
                  onChange={(e) => handleInputChange('currentAfCnt', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.currentAfCnt?.max || ''}
                  onChange={(e) => handleInputChange('currentAfCnt', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* ATH AF Count */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">ATH AF Count</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.athAfCnt?.min || ''}
                  onChange={(e) => handleInputChange('athAfCnt', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.athAfCnt?.max || ''}
                  onChange={(e) => handleInputChange('athAfCnt', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ATL Market Cap */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">ATL MC ($k)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min (K)"
                  value={getDisplayValue(filters.atlMc?.min, 'atlMc')}
                  onChange={(e) => handleInputChange('atlMc', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max (K)"
                  value={getDisplayValue(filters.atlMc?.max, 'atlMc')}
                  onChange={(e) => handleInputChange('atlMc', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* ATH Market Cap */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">ATH MC ($k)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min (K)"
                  value={getDisplayValue(filters.athMc?.min, 'athMc')}
                  onChange={(e) => handleInputChange('athMc', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max (K)"
                  value={getDisplayValue(filters.athMc?.max, 'athMc')}
                  onChange={(e) => handleInputChange('athMc', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Market Cap */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Current MC ($k)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min (K)"
                  value={getDisplayValue(filters.mc?.min, 'mc')}
                  onChange={(e) => handleInputChange('mc', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max (K)"
                  value={getDisplayValue(filters.mc?.max, 'mc')}
                  onChange={(e) => handleInputChange('mc', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Current Token Amount */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Current Token Amount (M, million)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min (M)"
                  value={getDisplayValue(filters.currentTokenAmountInDecimals?.min, 'currentTokenAmountInDecimals')}
                  onChange={(e) => handleInputChange('currentTokenAmountInDecimals', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max (M)"
                  value={getDisplayValue(filters.currentTokenAmountInDecimals?.max, 'currentTokenAmountInDecimals')}
                  onChange={(e) => handleInputChange('currentTokenAmountInDecimals', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ATH Token Amount */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">ATH Token Amount (M, million)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min (M)"
                  value={getDisplayValue(filters.athTokenAmountInDecimals?.min, 'athTokenAmountInDecimals')}
                  onChange={(e) => handleInputChange('athTokenAmountInDecimals', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max (M)"
                  value={getDisplayValue(filters.athTokenAmountInDecimals?.max, 'athTokenAmountInDecimals')}
                  onChange={(e) => handleInputChange('athTokenAmountInDecimals', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Total Used USD */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Total Used USD</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.totalUsedUsd?.min || ''}
                  onChange={(e) => handleInputChange('totalUsedUsd', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.totalUsedUsd?.max || ''}
                  onChange={(e) => handleInputChange('totalUsedUsd', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          {/* Row 5 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Dev Cluster Count */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Dev Cluster Count</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.devClusterCnt?.min || ''}
                  onChange={(e) => handleInputChange('devClusterCnt', 'min', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.devClusterCnt?.max || ''}
                  onChange={(e) => handleInputChange('devClusterCnt', 'max', e.target.value)}
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
            {/* Cumulative Conviction At Same Time Rank */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Cumulative Conviction At Same Time Rank
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.cumulativeConvictionAtSameTimeRank?.min || 0}
                  onChange={(e) =>
                    handleInputChange(
                      'cumulativeConvictionAtSameTimeRank',
                      'min',
                      e.target.value,
                    )
                  }
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.cumulativeConvictionAtSameTimeRank?.max || ''}
                  onChange={(e) =>
                    handleInputChange(
                      'cumulativeConvictionAtSameTimeRank',
                      'max',
                      e.target.value,
                    )
                  }
                  className="flex-1 w-[100px] px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          </div>

          
                       {/* Time Filter */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="space-y-2">
               <label className="block text-sm font-medium text-gray-300">First Buy Time (min, minute)</label>
               <div className="flex items-center bg-gray-700 rounded-md p-1">
                 <div className="flex-shrink-0">
                   <button
                     onClick={() => setFilters(prev => ({ 
                       ...prev, 
                       timeFilter: 'ALL',
                       customTimeMinutes: undefined // ALL 버튼 클릭 시 min/max 초기화
                     }))}
                     className={`px-2 py-2 text-sm font-medium rounded transition-all duration-200 ${
                       !filters.customTimeMinutes?.min && !filters.customTimeMinutes?.max
                         ? 'bg-amber-700 text-white shadow-sm'
                         : 'text-gray-300 hover:text-white hover:bg-gray-600'
                     }`}
                   >
                     ALL
                   </button>
                 </div>
                 
                 {/* 구분선 */}
                 <div className="w-px h-6 bg-gray-600 mx-2 flex-shrink-0"></div>
                 
                 {/* 커스텀 시간 입력 (분 단위, min/max) */}
                 <div className="flex items-center space-x-2 flex-1">
                   <input
                     type="number"
                     placeholder="Min (m)"
                     value={filters.customTimeMinutes?.min || ''}
                     onChange={(e) => {
                       const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                       setFilters(prev => ({ 
                         ...prev, 
                         customTimeMinutes: {
                           ...prev.customTimeMinutes,
                           min: value
                         },
                         timeFilter: 'ALL' // 커스텀 입력 시 항상 ALL로 설정
                       }));
                     }}
                     className={`flex-1 px-2 py-2 text-sm bg-gray-600 border rounded text-white placeholder-gray-400 focus:outline-none transition-colors ${
                       filters.customTimeMinutes?.min !== undefined
                         ? 'border-amber-600 border-2 focus:border-amber-400' 
                         : 'border-gray-500 focus:border-gray-400'
                     }`}
                     min="0"
                     step="1"
                     style={{
                      width: '100px',
                     }}
                   />
                   <span className="text-sm text-gray-300 flex-shrink-0">~</span>
                   <input
                     type="number"
                     placeholder="Max (m)"
                     value={filters.customTimeMinutes?.max || ''}
                     onChange={(e) => {
                       const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                       setFilters(prev => ({ 
                         ...prev, 
                         customTimeMinutes: {
                           ...prev.customTimeMinutes,
                           max: value
                         },
                         timeFilter: 'ALL' // 커스텀 입력 시 항상 ALL로 설정
                       }));
                     }}
                     className={`flex-1 px-2 py-2 text-sm bg-gray-600 border rounded text-white placeholder-gray-400 focus:outline-none transition-colors ${
                       filters.customTimeMinutes?.max !== undefined
                         ? 'border-amber-600 border-2 focus:border-amber-400' 
                         : 'border-gray-500 focus:border-gray-400'
                     }`}
                     min="0"
                     step="1"
                     style={{
                      width: '100px',
                     }}
                   />
                 </div>
               </div>
             </div>

             {/* Last Tx Time Time Filter */}
             <div className="space-y-2">
               <label className="block text-sm font-medium text-gray-300">Last Tx Time (min, minute)</label>
               <div className="flex items-center bg-gray-700 rounded-md p-1">
                 <div className="flex-shrink-0">
                   <button
                     onClick={() => setFilters(prev => ({ 
                       ...prev, 
                       lastUpdatedTimeFilter: 'ALL',
                       lastUpdatedCustomTimeMinutes: undefined // ALL 버튼 클릭 시 min/max 초기화
                     }))}
                     className={`px-2 py-2 text-sm font-medium rounded transition-all duration-200 ${
                       !filters.lastUpdatedCustomTimeMinutes?.min && !filters.lastUpdatedCustomTimeMinutes?.max
                         ? 'bg-amber-700 text-white shadow-sm'
                         : 'text-gray-300 hover:text-white hover:bg-gray-600'
                     }`}
                   >
                     ALL
                   </button>
                 </div>
                 
                 {/* 구분선 */}
                 <div className="w-px h-6 bg-gray-600 mx-2 flex-shrink-0"></div>
                 
                 {/* 커스텀 시간 입력 (분 단위, min/max) */}
                 <div className="flex items-center space-x-2 flex-1">
                   <input
                     type="number"
                     placeholder="Min (m)"
                     value={filters.lastUpdatedCustomTimeMinutes?.min || ''}
                     onChange={(e) => {
                       const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                       setFilters(prev => ({ 
                         ...prev, 
                         lastUpdatedCustomTimeMinutes: {
                           ...prev.lastUpdatedCustomTimeMinutes,
                           min: value
                         },
                         lastUpdatedTimeFilter: 'ALL' // 커스텀 입력 시 항상 ALL로 설정
                       }));
                     }}
                     className={`flex-1 px-2 py-2 text-sm bg-gray-600 border rounded text-white placeholder-gray-400 focus:outline-none transition-colors ${
                       filters.lastUpdatedCustomTimeMinutes?.min !== undefined
                         ? 'border-amber-600 border-2 focus:border-amber-400' 
                         : 'border-gray-500 focus:border-gray-400'
                     }`}
                     min="0"
                     step="1"
                     style={{
                      width: '100px',
                     }}
                   />
                   <span className="text-sm text-gray-300 flex-shrink-0">~</span>
                   <input
                     type="number"
                     placeholder="Max (m)"
                     value={filters.lastUpdatedCustomTimeMinutes?.max || ''}
                     onChange={(e) => {
                       const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                       setFilters(prev => ({ 
                         ...prev, 
                         lastUpdatedCustomTimeMinutes: {
                           ...prev.lastUpdatedCustomTimeMinutes,
                           max: value
                         },
                         lastUpdatedTimeFilter: 'ALL' // 커스텀 입력 시 항상 ALL로 설정
                       }));
                     }}
                     className={`flex-1 px-2 py-2 text-sm bg-gray-600 border rounded text-white placeholder-gray-400 focus:outline-none transition-colors ${
                       filters.lastUpdatedCustomTimeMinutes?.max !== undefined
                         ? 'border-amber-600 border-2 focus:border-amber-400' 
                         : 'border-gray-500 focus:border-gray-400'
                     }`}
                     min="0"
                     step="1"
                     style={{
                      width: '100px',
                     }}
                   />
                 </div>
               </div>
             </div>
           </div>

          
        </div>

        <div className="flex justify-end items-center mt-8">
          <div className="flex space-x-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
            >
              초기화
            </button>
            <button
              onClick={handleApplyFilter}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors"
            >
              필터 적용
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
